import memoize from 'memoize-one';
import React, {memo, useContext, useEffect, useMemo, useRef} from 'react';
import {Scrollbars} from 'react-custom-scrollbars';
import {useIntl} from 'react-intl';
import {useAbsoluteLayout, useTable} from 'react-table';
import {areEqual, FixedSizeList, VariableSizeList} from 'react-window';
import {MapContext, ThemeContext} from '../../apiContexts';
import {ResultLength, Styles} from './style';

const ROW_HEIGHT = 45;

const createItemData = memoize((items, selectItem) => ({
  items,
  selectItem,
}));

// `getRowHeight(linha)` é opcional: sem ele toda linha tem 45 px (lista de altura fixa). Com ele a lista passa a
// ter altura por linha, para uma coluna com `wrap: true` mostrar o texto inteiro em várias linhas.
const ReactTable = ({
  data,
  columns,
  selectItem,
  listHeight,
  innerRef,
  scrollToIndex,
  getRowHeight,
  ...props
}) => {
  const {getTableProps, headerGroups, rows, prepareRow} = useTable(
      {columns, data},
      useAbsoluteLayout,
  );

  const listRef = useRef();
  const scrollRef = useRef();
  const headerRef = useRef();

  const intl = useIntl();
  const theme = useContext(ThemeContext);

  const {selectedRow} = useContext(MapContext);

  const itemData = createItemData(rows, selectItem);

  // Altura de cada linha e a posição do topo dela: a rolagem até uma linha usa o topo, que com altura fixa é
  // índice * 45.
  const {heights, offsets} = useMemo(() => {
    const heights = rows.map((row) => (getRowHeight ? getRowHeight(row.original) : ROW_HEIGHT));
    const offsets = [];
    heights.reduce((top, height, index) => {
      offsets[index] = top;
      return top + height;
    }, 0);
    return {heights, offsets};
  }, [rows, getRowHeight]);

  useEffect(() => {
    if (listRef.current && listRef.current.resetAfterIndex) {
      listRef.current.resetAfterIndex(0);
    }
  }, [heights]);

  const RenderRow = memo(({data, index, style}) => {
    const {items, selectItem} = data;
    const row = items[index];
    prepareRow(row);
    const {original} = row;

    return (
      <div
        {...row.getRowProps({style})}
        className={`tr ${original.isActive ? 'active' : ''}`}>
        {row.cells.map((cell) => {
          return (
            // eslint-disable-next-line react/jsx-key
            <div
              {...cell.getCellProps()}
              className={`td ${cell.column.wrap ? 'wrap' : ''}`}
              onClick={() => selectItem({index, data: original})}>
              {cell.render('Cell')}
            </div>
          );
        })}
      </div>
    );
  }, areEqual);

  RenderRow.displayName = 'RenderRow';

  useEffect(() => {
    if (selectedRow && selectedRow.scroll) {
      const index = data.findIndex((e) => e.id == selectedRow.id);
      selectItem({index});
      scrollRef.current.scrollTop(offsets[index] || 0);
    }
  }, [selectedRow]);

  useEffect(() => {
    if (scrollToIndex == null) return;
    scrollRef.current.scrollTop(offsets[scrollToIndex] || 0);
  }, [scrollToIndex]);

  const handleScroll = ({target}) => {
    const {scrollTop, scrollLeft} = target;
    listRef.current.scrollTo(scrollTop);
    headerRef.current.scrollTo({left: scrollLeft});
  };
  return (
    <Styles ref={innerRef} {...getTableProps()} className="table" theme={theme}>
      <div ref={headerRef} className="header">
        {headerGroups.map((headerGroup) => (
          // eslint-disable-next-line react/jsx-key
          <div {...headerGroup.getHeaderGroupProps()} className="tr">
            {headerGroup.headers.map((column) => (
              // eslint-disable-next-line react/jsx-key
              <div {...column.getHeaderProps()} className="th">
                {column.render('Header')}
              </div>
            ))}
          </div>
        ))}
      </div>
      <Scrollbars
        ref={scrollRef}
        autoHeight={true}
        autoHide={false}
        autoHeightMax={listHeight ? listHeight - 80 || 0 : 150}
        onScroll={handleScroll}>
        {getRowHeight ? (
          <VariableSizeList
            className="body"
            height={listHeight ? listHeight - 80 || 150 : 150}
            itemCount={data.length}
            itemData={itemData}
            itemSize={(index) => heights[index]}
            estimatedItemSize={ROW_HEIGHT}
            ref={listRef}
            style={{overflow: false}}
            width={'100%'}>
            {RenderRow}
          </VariableSizeList>
        ) : (
          <FixedSizeList
            className="body"
            height={listHeight ? listHeight - 80 || 150 : 150}
            itemCount={data.length}
            itemData={itemData}
            itemSize={ROW_HEIGHT}
            ref={listRef}
            style={{overflow: false}}
            width={'100%'}>
            {RenderRow}
          </FixedSizeList>
        )}
      </Scrollbars>
      <ResultLength>{data.length} {intl.formatMessage({id: 'sharedResults'})}</ResultLength>
    </Styles>
  );
};

export default ReactTable;
